import React, { useRef, useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { Alert, Badge, TextInput, Toggle, Zap } from '@opendate-app/setlist'

const DynamicPricingSection = ({
  ticketTypeForForm,
  onTicketTypeChange,
  team,
  seedsBoundsFromPrice
}) => {
  const sliderRef = useRef(null)
  const [dragging, setDragging] = useState(null)
  const previousBasePriceRef = useRef(null)

  const basePrice = parseFloat(String(ticketTypeForForm.base_price_two_decimals || '').replace(/,/g, '')) || 0
  const minPrice = parseFloat(ticketTypeForForm.dynamic_pricing_min) || 0
  const maxPrice = parseFloat(ticketTypeForForm.dynamic_pricing_max) || 0

  useEffect(() => {
    if (!seedsBoundsFromPrice) return
    if (!ticketTypeForForm.dynamic_pricing_enabled) {
      previousBasePriceRef.current = null
      return
    }
    if (basePrice === 0) return
    if (dragging) return

    const timeoutId = setTimeout(() => {
      const prevPrice = previousBasePriceRef.current

      if (prevPrice === null || prevPrice !== basePrice) {
        const currentMax = parseFloat(ticketTypeForForm.dynamic_pricing_max) || 0

        if (currentMax === 0 || currentMax <= basePrice) {
          const updated = Object.assign({}, ticketTypeForForm, {
            dynamic_pricing_min: basePrice.toFixed(2),
            dynamic_pricing_max: (basePrice * 3).toFixed(2)
          })
          onTicketTypeChange(updated)
          previousBasePriceRef.current = basePrice
        } else if (prevPrice !== null && prevPrice > 0) {
          const maxRatio = currentMax / prevPrice
          const newMax = Math.min(basePrice * 3, basePrice * maxRatio)

          if (Math.abs(newMax - currentMax) > 0.01) {
            const updated = Object.assign({}, ticketTypeForForm, {
              dynamic_pricing_min: basePrice.toFixed(2),
              dynamic_pricing_max: newMax.toFixed(2)
            })
            onTicketTypeChange(updated)
          } else {
            const updated = Object.assign({}, ticketTypeForForm, {
              dynamic_pricing_min: basePrice.toFixed(2)
            })
            onTicketTypeChange(updated)
          }
          previousBasePriceRef.current = basePrice
        } else {
          const updated = Object.assign({}, ticketTypeForForm, {
            dynamic_pricing_min: basePrice.toFixed(2)
          })
          onTicketTypeChange(updated)
          previousBasePriceRef.current = basePrice
        }
      }
    }, 300)

    return () => clearTimeout(timeoutId)
  }, [basePrice, dragging])

  useEffect(() => {
    if (dragging) {
      document.addEventListener('mousemove', handleMouseMove)
      document.addEventListener('mouseup', handleMouseUp)
      return () => {
        document.removeEventListener('mousemove', handleMouseMove)
        document.removeEventListener('mouseup', handleMouseUp)
      }
    }
  }, [dragging, ticketTypeForForm])

  if (!team.package?.dynamic_pricing) {
    return null
  }

  if (ticketTypeForForm.free) {
    return null
  }

  const maxPercentage = basePrice > 0 ? ((maxPrice - basePrice) / basePrice * 100) : 0

  const cardFinalCost = parseFloat(ticketTypeForForm.final_cost) || 0
  const cardTaxes = parseFloat(ticketTypeForForm.taxes) || 0
  const cardTotal = cardFinalCost + cardTaxes
  let maxAllInPrice = basePrice > 0 && cardTotal > 0
    ? cardTotal + (cardTotal * maxPercentage / 100)
    : (maxPrice || 0) * 1.25

  if (ticketTypeForForm.round_up && maxAllInPrice > 0) {
    maxAllInPrice = Math.ceil(maxAllInPrice)
  }

  const sliderRange = basePrice * 2
  const minPercent = 0
  const maxPercent = basePrice > 0 ? ((maxPrice - basePrice) / sliderRange) * 100 : 0

  const handleToggle = (e) => {
    const enabled = e.target.checked

    const defaultMin = basePrice.toFixed(2)
    const defaultMax = (basePrice * 3).toFixed(2)

    const updated = Object.assign({}, ticketTypeForForm, {
      dynamic_pricing_enabled: enabled,
      dynamic_pricing_min: enabled ? defaultMin : null,
      dynamic_pricing_max: enabled ? defaultMax : null
    })
    onTicketTypeChange(updated)
  }

  const handleMouseDown = (type) => (e) => {
    e.preventDefault()
    e.stopPropagation()
    setDragging(type)
  }

  const handleMouseMove = (e) => {
    if (!dragging || !sliderRef.current) return
    if (dragging !== 'max') return

    const rect = sliderRef.current.getBoundingClientRect()
    const x = e.clientX - rect.left
    const percent = Math.max(0, Math.min(100, (x / rect.width) * 100))

    let newPrice = basePrice + (percent / 100) * sliderRange

    newPrice = Math.round(newPrice)

    const sliderMax = basePrice * 3
    newPrice = Math.min(newPrice, sliderMax)

    if (newPrice >= basePrice && newPrice <= sliderMax) {
      const updated = Object.assign({}, ticketTypeForForm, {
        dynamic_pricing_max: newPrice.toFixed(2)
      })
      onTicketTypeChange(updated)
    }
  }

  const handleMouseUp = () => {
    setDragging(null)
  }

  const requiredMark = <span aria-hidden="true" style={{ color: 'var(--sl-text-brand)' }}>&nbsp;*</span>

  return (
    <div className="form-group">
      <Toggle
        id="ticket-type-dynamic-pricing"
        onChange={handleToggle}
        checked={ticketTypeForForm.dynamic_pricing_enabled || false}
        label={(
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
            <Zap width={16} height={16} aria-hidden="true" />
            Dynamic pricing
          </span>
        )}
      />

      {ticketTypeForForm.dynamic_pricing_enabled && (
        <div style={{
          marginTop: '16px',
          display: 'flex',
          flexDirection: 'column',
          gap: '16px',
          padding: '16px',
          backgroundColor: 'var(--sl-bg-secondary)',
          borderRadius: 'var(--sl-radius-lg)',
          border: '1px solid var(--sl-border-secondary)'
        }}>
          <Alert
            color="brand"
            icon={Zap}
            title="Dynamic Pricing lets your ticket price rise with demand."
            description={(
              <>
                Set the maximum percentage increase from your base price. We&apos;ll automatically adjust the ticket price as demand changes. {basePrice > 0 && maxPercentage !== 0 && (
                  <span style={{ color: 'var(--sl-text-brand)', fontWeight: 500 }}>
                    (currently +{maxPercentage.toFixed(0)}%)
                  </span>
                )}
                <br />
                <em>Note: This percentage range will apply to your base price and all price changes.</em>
              </>
            )}
          />

          {ticketTypeForForm.dynamic_pricing_current_price && (
            <div>
              <Badge color="brand" size="lg">
                Current dynamic price: ${parseFloat(ticketTypeForForm.dynamic_pricing_current_price).toFixed(2)}
              </Badge>
            </div>
          )}

          <TextInput
            type="number"
            label={<>Quantity{requiredMark}</>}
            value={ticketTypeForForm.available || ''}
            onChange={(e) => {
              const updated = Object.assign({}, ticketTypeForForm, {
                available: e.target.value
              })
              onTicketTypeChange(updated)
            }}
          />

          <div>
            <div
              style={{
                fontSize: '14px',
                fontWeight: 500,
                lineHeight: '20px',
                color: 'var(--sl-text-secondary)',
                marginBottom: '8px'
              }}
            >
              Maximum percentage increase
              {requiredMark}
            </div>

            <div style={{
              padding: '8px 0',
              position: 'relative',
              height: '69px'
            }}>
              <div
                ref={sliderRef}
                style={{
                  position: 'relative',
                  height: '8px',
                  backgroundColor: 'var(--sl-bg-tertiary)',
                  borderRadius: '9999px',
                  width: '100%'
                }}
              >
                <div style={{
                  position: 'absolute',
                  left: `${minPercent}%`,
                  right: `${100 - maxPercent}%`,
                  height: '100%',
                  backgroundColor: 'var(--sl-bg-brand-solid)',
                  borderRadius: '9999px',
                  top: 0
                }} />

                <div
                  data-handle="min"
                  style={{
                    position: 'absolute',
                    left: `${minPercent}%`,
                    top: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: '24px',
                    height: '24px',
                    zIndex: 2
                  }}
                >
                  <div style={{
                    width: '24px',
                    height: '24px',
                    backgroundColor: 'var(--sl-bg-primary)',
                    border: '2px solid var(--sl-border-primary)',
                    borderRadius: '9999px'
                  }} />
                </div>

                <div
                  data-handle="max"
                  style={{
                    position: 'absolute',
                    left: `${maxPercent}%`,
                    top: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: '24px',
                    height: '24px',
                    zIndex: dragging === 'max' ? 4 : 3,
                    cursor: 'grab'
                  }}
                  onMouseDown={handleMouseDown('max')}
                >
                  <div style={{
                    width: '24px',
                    height: '24px',
                    backgroundColor: 'var(--sl-bg-primary)',
                    border: '2px solid var(--sl-border-brand)',
                    borderRadius: '9999px',
                    cursor: 'pointer'
                  }} />
                  <div style={{
                    position: 'absolute',
                    top: '44px',
                    transform: `translateY(-50%)`,
                    textAlign: maxPercent < 20 ? 'left' : maxPercent > 80 ? 'right' : 'center',
                    ...(maxPercent < 20
                      ? { left: '8px' }
                      : maxPercent > 80
                        ? { right: '8px' }
                        : { left: '50%', transform: 'translate(-50%, -50%)' }),
                    whiteSpace: 'nowrap',
                    pointerEvents: 'none',
                    display: 'flex',
                    flexDirection: 'column'
                  }}>
                    <div style={{
                      fontSize: '16px',
                      fontWeight: 500,
                      lineHeight: '24px',
                      color: 'var(--sl-text-primary)'
                    }}>
                      ${(maxPrice || 0).toFixed(2)}
                      {basePrice > 0 && (
                        <span style={{ fontSize: '12px', color: 'var(--sl-text-brand)', marginLeft: '4px' }}>
                          (+{maxPercentage.toFixed(0)}%)
                        </span>
                      )}
                    </div>
                    <div style={{
                      fontSize: '14px',
                      lineHeight: '20px',
                      color: 'var(--sl-text-tertiary)',
                      marginTop: '2px'
                    }}>
                      ${maxAllInPrice.toFixed(2)}
                    </div>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
      )}
    </div>
  )
}

DynamicPricingSection.propTypes = {
  ticketTypeForForm: PropTypes.object.isRequired,
  onTicketTypeChange: PropTypes.func.isRequired,
  team: PropTypes.object.isRequired,
  seedsBoundsFromPrice: PropTypes.bool
}

DynamicPricingSection.defaultProps = {
  seedsBoundsFromPrice: true
}

export default DynamicPricingSection
